import axios from 'axios';

const api = axios.create({
  baseURL: '/api',
  timeout: 30000,
});

// Attach JWT token
api.interceptors.request.use(config => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

// Handle 401 globally
api.interceptors.response.use(
  res => res,
  err => {
    if (err.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      if (window.location.pathname.startsWith('/tm')) {
        window.location.href = '/tm/login';
      } else {
        window.location.href = '/login';
      }
    }
    return Promise.reject(err);
  }
);

// ── AUTH ──
export const login = (username, password) =>
  api.post('/auth/login', { username, password }).then(r => r.data);
export const getMe = () => api.get('/auth/me').then(r => r.data);
export const changePassword = (data) => api.post('/auth/change-password', data).then(r => r.data);

// ── DASHBOARD ──
export const getDashboard = () => api.get('/reports/dashboard').then(r => r.data);

// ── CLIENTS ──
export const getClients = (params) => api.get('/clients', { params }).then(r => r.data);
export const getClient = (id) => api.get(`/clients/${id}`).then(r => r.data);
export const createClient = (data) => api.post('/clients', data).then(r => r.data);
export const updateClient = (id, data) => api.put(`/clients/${id}`, data).then(r => r.data);
export const deleteClient = (id, force = false) => api.delete(`/clients/${id}${force ? '?force=true' : ''}`).then(r => r.data);
export const getClientDependencies = (id) => api.get(`/clients/${id}/dependencies`).then(r => r.data);
export const generateTrackingToken = (id) => api.post(`/clients/${id}/tracking-token`).then(r => r.data);
export const getTrackingData = (token) => api.get(`/tracking/${token}`).then(r => r.data);

// ── PROJECTS ──
export const getProjects = (params) => api.get('/projects', { params }).then(r => r.data);
export const getProject = (id) => api.get(`/projects/${id}`).then(r => r.data);
export const createProject = (data) => api.post('/projects', data).then(r => r.data);
export const updateProject = (id, data) => api.put(`/projects/${id}`, data).then(r => r.data);
export const getProjectsDueSoon = () => api.get('/projects/due-soon').then(r => r.data);
export const getProjectsOverdue = () => api.get('/projects/overdue').then(r => r.data);
export const updateProjectStatus = (id, status) => api.put(`/projects/${id}/status`, { status }).then(r => r.data);
export const archiveProject = (id) => api.put(`/projects/${id}/archive`).then(r => r.data);
export const deleteProject = (id) => api.delete(`/projects/${id}`).then(r => r.data);
export const getProjectSummary = (id) => api.get(`/projects/${id}/summary`).then(r => r.data);

// ── SUBSCRIPTIONS ──
export const getSubscriptions = (params) => api.get('/subscriptions', { params }).then(r => r.data);
export const getSubscription = (id) => api.get(`/subscriptions/${id}`).then(r => r.data);
export const createSubscription = (data) => api.post('/subscriptions', data).then(r => r.data);
export const updateSubscription = (id, data) => api.put(`/subscriptions/${id}`, data).then(r => r.data);
export const addDelivery = (subscriptionId, data) =>
  api.post(`/subscriptions/${subscriptionId}/deliver`, data).then(r => r.data);
export const getExpiringSubs = () => api.get('/subscriptions/expiring-soon').then(r => r.data);
export const generateSubInvoice = (id) => api.post(`/subscriptions/${id}/generate-invoice`).then(r => r.data);
export const deleteSubscription = (id) => api.delete(`/subscriptions/${id}`).then(r => r.data);
export const cancelSubscription = (id, reason) => api.post(`/subscriptions/${id}/cancel`, { reason }).then(r => r.data);
export const refundCancelSubscription = (id, reason, refund_iqd) => api.post(`/subscriptions/${id}/refund-cancel`, { reason, refund_iqd }).then(r => r.data);

// ── INVOICES ──
export const getInvoices = (params) => api.get('/invoices', { params }).then(r => r.data);
export const getInvoice = (id) => api.get(`/invoices/${id}`).then(r => r.data);
export const createInvoice = (data) => api.post('/invoices', data).then(r => r.data);
export const updateInvoice = (id, data) => api.put(`/invoices/${id}`, data).then(r => r.data);
export const recordPayment = (invoiceId, data) =>
  api.post(`/invoices/${invoiceId}/pay`, data).then(r => r.data);
export const getOutstandingInvoices = () => api.get('/invoices/outstanding').then(r => r.data);

export const cancelInvoice = (id, reason) =>
  api.post(`/invoices/${id}/cancel`, { reason }).then(r => r.data);
export const deleteInvoice = (id) => api.delete(`/invoices/${id}`).then(r => r.data);
export const reversePayment = (invoiceId, paymentId, reason) =>
  api.post(`/invoices/${invoiceId}/payments/${paymentId}/reverse`, { reason }).then(r => r.data);
export const reconcileInvoices = () =>
  api.post('/invoices/reconcile').then(r => r.data);

// ── EXPENSES ──
export const getExpenses = (params) => api.get('/expenses', { params }).then(r => r.data);
export const createExpense = (data) => api.post('/expenses', data).then(r => r.data);
export const updateExpense = (id, data) => api.put(`/expenses/${id}`, data).then(r => r.data);
export const deleteExpense = (id) => api.delete(`/expenses/${id}`).then(r => r.data);


// ── REPORTS ──
export const getMonthlyReport = (paramsOrMonth) => {
  const params = typeof paramsOrMonth === 'string' ? { month: paramsOrMonth } : paramsOrMonth;
  return api.get('/reports/monthly', { params }).then(r => r.data);
};
export const getClientLTV = () => api.get('/reports/client-ltv').then(r => r.data);
export const getOverdueInvoices = () => api.get('/reports/overdue-invoices').then(r => r.data);
export const getInvoiceStatement = (params) => api.get('/reports/invoice-statement', { params }).then(r => r.data);
export const getCommissions = (params) => api.get('/reports/commissions', { params }).then(r => r.data);
export const getDailySales = (params) => api.get('/reports/daily-sales', { params }).then(r => r.data);
export const getDailyDeliveries = (params) => api.get('/reports/daily-deliveries', { params }).then(r => r.data);
export const getRetentionReport = (params) => api.get('/reports/retention', { params }).then(r => r.data);

// ── SETTINGS ──
export const getExchangeRates = () => api.get('/settings/exchange-rates').then(r => r.data);
export const getCurrentRate = () => api.get('/settings/exchange-rates/current').then(r => r.data);
export const setExchangeRate = (data) => api.post('/settings/exchange-rates', data).then(r => r.data);
export const getAITools = () => api.get('/settings/ai-tools').then(r => r.data);
export const createAITool = (data) => api.post('/settings/ai-tools', data).then(r => r.data);
export const updateAITool = (id, data) => api.put(`/settings/ai-tools/${id}`, data).then(r => r.data);
export const getPackages = () => api.get('/settings/packages').then(r => r.data);

// ── EXPENSE CATEGORIES ──
export const getExpenseCategories = () => api.get('/settings/expense-categories').then(r => r.data);
export const createExpenseCategory = (data) => api.post('/settings/expense-categories', data).then(r => r.data);
export const updateExpenseCategory = (id, data) => api.put(`/settings/expense-categories/${id}`, data).then(r => r.data);
export const deleteExpenseCategory = (id) => api.delete(`/settings/expense-categories/${id}`).then(r => r.data);


// ── HELPERS ──
export const formatIQD = (n) => new Intl.NumberFormat('en-US').format(Math.round(n || 0)) + ' د.ع';
export const formatUSD = (n) => '$' + new Intl.NumberFormat('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);

export default api;

// ── NOTIFICATIONS ──
export const getNotifications = () => api.get('/notifications').then(r => r.data);
export const getMyNotifications = () => api.get('/notifications/my').then(r => r.data);
export const markNotificationRead = (id) => api.put(`/notifications/${id}/read`).then(r => r.data);
export const markAllNotificationsRead = () => api.put('/notifications/read-all').then(r => r.data);

// ── AUDIT ──
export const getAuditLog = (params) => api.get('/audit', { params }).then(r => r.data);

// ── SYSTEM SETTINGS ──
export const getSystemSettings = () => api.get('/settings/system').then(r => r.data);
export const saveSystemSettings = (data) => api.post('/settings/system', data).then(r => r.data);

// ── DELIVERABLES ──
export const getDeliverables = (params) => api.get('/deliverables', { params }).then(r => r.data);
export const createDeliverable = (data) => api.post('/deliverables', data).then(r => r.data);
export const deleteDeliverable = (id) => api.delete(`/deliverables/${id}`).then(r => r.data);
export const updateDeliverable = (id, data) => api.put(`/deliverables/${id}`, data).then(r => r.data);

// ── USERS MANAGEMENT ──
export const getUsers = () => api.get('/auth/users').then(r => r.data);
export const createUser = (data) => api.post('/auth/users', data).then(r => r.data);
export const updateUser = (id, data) => api.put(`/auth/users/${id}`, data).then(r => r.data);
export const deleteUser = (id, force = false) => api.delete(`/auth/users/${id}?force=${force}`).then(r => r.data);

// ── PACKAGES CRUD ──
export const getAllPackages = (params) => api.get('/packages', { params }).then(r => r.data);
export const createPackage = (data) => api.post('/packages', data).then(r => r.data);
export const updatePackage = (id, data) => api.put(`/packages/${id}`, data).then(r => r.data);
export const deletePackage = (id) => api.delete(`/packages/${id}`).then(r => r.data);

// ── EMPLOYEES ──
export const getEmployees = () => api.get('/employees').then(r => r.data);
export const createEmployee = (data) => api.post('/employees', data).then(r => r.data);
export const updateEmployee = (id, data) => api.put(`/employees/${id}`, data).then(r => r.data);
export const deleteEmployee = (id) => api.delete(`/employees/${id}`).then(r => r.data);
export const getTeamPerformance = (params) => api.get('/employees/performance', { params }).then(r => r.data);

// ── TASKS MANAGEMENT ──
export const getTasks = (params) => api.get('/tasks', { params }).then(r => r.data);
export const createTask = (data) => api.post('/tasks', data).then(r => r.data);
export const updateTask = async (id, data) => (await api.put(`/tasks/${id}`, data)).data;
export const deleteTask = async (id) => (await api.delete(`/tasks/${id}`)).data;
export const transferTask = async (id, data) => (await api.post(`/tasks/${id}/transfer`, data)).data;
export const uploadTaskAttachment = async (id, file, type = 'task') => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('type', type);
  return (await api.post(`/tasks/${id}/attachments`, formData, { 
    headers: { 'Content-Type': 'multipart/form-data' },
    timeout: 5 * 60 * 1000 // 5 minutes for large files
  })).data;
};
export const getTaskAttachments = async (id) => (await api.get(`/tasks/${id}/attachments`)).data;
export const getTaskComments = async (id) => (await api.get(`/tasks/${id}/comments`)).data;
export const addTaskComment = async (id, data) => (await api.post(`/tasks/${id}/comments`, data)).data;

export const getTaskChecklists = async (id) => (await api.get(`/tasks/${id}/checklists`)).data;
export const addTaskChecklist = async (id, content) => (await api.post(`/tasks/${id}/checklists`, { content })).data;
export const updateTaskChecklist = async (taskId, checklistId, is_completed) => (await api.put(`/tasks/${taskId}/checklists/${checklistId}`, { is_completed })).data;
export const deleteTaskChecklist = async (taskId, checklistId) => (await api.delete(`/tasks/${taskId}/checklists/${checklistId}`)).data;

// ── NEW TEAM PERFORMANCE API ──
export const getNewTeamPerformance = (params) => api.get('/team-performance/monthly', { params }).then(r => r.data);
export const getEmployeeCompletedTasks = (employeeId, params) => api.get(`/team-performance/${employeeId}/tasks`, { params }).then(r => r.data);

export const getEmployeeDeliverables = (id, params) => api.get(`/employees/${id}/deliverables`, { params }).then(r => r.data);
export const generateEmployeeTrackingToken = (id) => api.post(`/employees/${id}/tracking-token`).then(r => r.data);
export const getEmployeeTrackingData = (token, month) => api.get(`/tracking/employee/${token}`, { params: { month } }).then(r => r.data);
export const getSidebarStats = () => api.get('/settings/sidebar-stats').then(r => r.data);
